import { toast } from "react-toastify";
import NEW_API from "../../Api/NewApi";

// ---------------------------------------------------------------------------
// Prospects/Clients of the mobile application. Since the single-API merge the
// accounts live in THIS backend — the thunks call /api/customers directly
// (src/api/customer/*), scoped server-side to the caller's organization.
// ---------------------------------------------------------------------------

const KNOWN_ERRORS = [
  "EMAIL_TAKEN",
  "ORGANIZATION_REQUIRED",
  "AFFILIATE_NOT_LINKED",
  "PASSWORD_TOO_SHORT",
  "HAS_ORDERS",
  "VALIDATION_ERROR",
  "RESELLER_NO_ORGANIZATION",
  "APP_API_UNREACHABLE",
];

const errorCode = (error) =>
  error?.response?.data?.error?.message || error?.response?.data?.error?.name;

const toastError = (error, t, fallback) => {
  const code = errorCode(error);
  if (code === "EMAIL_TAKEN") toast.error(t("This email is already in use."));
  else if (KNOWN_ERRORS.includes(code)) toast.error(t(`${code}`));
  else toast.error(t(fallback));
};

/** Espace clients filter bar → { organizationId, businessAffiliateId }. */
export const setAppCustomersScope = (scope) => ({
  type: "SET_APP_CUSTOMERS_SCOPE",
  payload: scope,
});

/**
 * The filter bar's scope as request params — the server narrows the caller's
 * organizations with them (and ignores them for a reseller).
 */
export const scopeParams = (getState) => {
  const scope = getState().appCustomersReducer?.scope ?? {};
  return {
    organizationId: scope.organizationId || undefined,
    businessAffiliateId: scope.businessAffiliateId || undefined,
  };
};

export const fetchAppCustomers =
  (search, filters, page = 1, signal) =>
  async (dispatch, getState) => {
    try {
      if (page === 1) dispatch({ type: "FETCH_APP_CUSTOMERS_LOADING" });
      const { data } = await NEW_API.get("/api/customers", {
        params: {
          search: search || undefined,
          role: filters?.role?.code || undefined,
          // Client list filters (Commercial, Contact, Modèle, Tranche taux marge).
          salesRep: filters?.salesRep?.code || undefined,
          contact: filters?.contact?.code || undefined,
          model: filters?.model?.code || undefined,
          marginRange: filters?.marginRange?.code || undefined,
          page,
          pageSize: 25,
          ...scopeParams(getState),
        },
        signal,
      });
      dispatch({
        type:
          page === 1
            ? "FETCH_APP_CUSTOMERS_SUCCESS"
            : "FETCH_APP_CUSTOMERS_LOAD_MORE_SUCCESS",
        payload: data,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
      dispatch({ type: "FETCH_APP_CUSTOMERS_ERROR", payload: error?.message });
    }
  };

export const createAppCustomer =
  (customer, t, callback) => async (dispatch) => {
    try {
      dispatch({ type: "CREATE_APP_CUSTOMER_LOADING" });
      const { data } = await NEW_API.post("/api/customers", customer);
      dispatch({ type: "CREATE_APP_CUSTOMER_SUCCESS", payload: data?.data });
      toast.success(t("Customer successfully created!"));
      if (callback) callback();
    } catch (error) {
      console.log(error);
      dispatch({ type: "CREATE_APP_CUSTOMER_ERROR" });
      toastError(error, t, "An error has occurred!");
    }
  };

export const updateAppCustomer =
  (id, customer, t, callback) => async (dispatch) => {
    try {
      dispatch({ type: "UPDATE_APP_CUSTOMER_LOADING" });
      const { data } = await NEW_API.put(`/api/customers/${id}`, customer);
      dispatch({ type: "UPDATE_APP_CUSTOMER_SUCCESS", payload: data?.data });
      toast.success(t("Customer successfully updated!"));
      if (callback) callback();
    } catch (error) {
      console.log(error);
      dispatch({ type: "UPDATE_APP_CUSTOMER_ERROR" });
      toastError(error, t, "An error has occurred!");
    }
  };

export const deleteAppCustomer = (id, t) => async (dispatch) => {
  try {
    dispatch({ type: "DELETE_APP_CUSTOMER_LOADING" });
    await NEW_API.delete(`/api/customers/${id}`);
    dispatch({ type: "DELETE_APP_CUSTOMER_SUCCESS", payload: id });
    toast.success(t("Customer successfully deleted!"));
  } catch (error) {
    console.log(error);
    dispatch({ type: "DELETE_APP_CUSTOMER_ERROR" });
    toastError(error, t, "An error has occurred!");
  }
};

// Light rows (id, names, contact info, markers) for the Carte tab — every
// scoped customer that has at least one marker.
export const fetchAppCustomersMap = (signal) => async (dispatch, getState) => {
  try {
    dispatch({ type: "FETCH_APP_CUSTOMERS_MAP_LOADING" });
    const { data } = await NEW_API.get("/api/customers/map", {
      params: scopeParams(getState),
      signal,
    });
    dispatch({
      type: "FETCH_APP_CUSTOMERS_MAP_SUCCESS",
      payload: data?.data ?? [],
    });
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
    console.log(error);
    dispatch({ type: "FETCH_APP_CUSTOMERS_MAP_ERROR" });
  }
};

// Headline counters above the list: total / created this year / with an app
// project created this year — scoped server-side like the list itself.
export const fetchAppCustomerStats =
  (role, signal) => async (dispatch, getState) => {
  try {
    dispatch({ type: "FETCH_APP_CUSTOMER_STATS_LOADING" });
    const { data } = await NEW_API.get("/api/customers/stats", {
      params: { role: role || undefined, ...scopeParams(getState) },
      signal,
    });
    dispatch({ type: "FETCH_APP_CUSTOMER_STATS_SUCCESS", payload: data?.data });
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
    console.log(error);
    dispatch({ type: "FETCH_APP_CUSTOMER_STATS_ERROR" });
  }
};
